/**
 * About Us — MadHat Media, Inc. company narrative.
 *
 * Split from the original About.tsx on 2026-05-26 in Public Pages
 * Refinements Phase 5; the product/protocol narrative now lives at
 * /about-ddarp.
 *
 * Section anchors:
 *   #company       – MadHat Media, Inc. + leadership + advisors + MadHat TV
 *   #engineering   – SapientSwift Solutions attribution
 *
 * Product-side anchors (#thesis, #thesis-detail, #architecture,
 * #substrate, #neutrality, #patent, #roadmap) trigger a client-side
 * navigate to /about-ddarp#<anchor> so legacy bookmarks survive the split.
 *
 * Phase 7 (2026-05-26) added advisors Riccardo Ricci and Claudio Antonini.
 */
import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  GlowCard,
  GradientText,
  PublicCTAPair,
} from '@/components/common';
import {
  Building2,
  MapPin,
  Calendar,
  FileText,
  Linkedin,
  Mail,
  Users,
  Briefcase,
  Award,
  Wrench,
  ExternalLink,
} from 'lucide-react';

// ─── Data ────────────────────────────────────────────────────────────────────

const LEADERSHIP = [
  {
    name: 'Benjamin Piilani',
    title: 'Founder & CEO',
    description:
      'Benjamin Piilani combines executive leadership with a scientific background in protocol design, network architecture, and theoretical physics. He oversees company strategy, operations, and the intellectual property portfolio, while pioneering innovations such as the Distributed Detection And Routing Protocol (DDARP).',
    initials: 'BP',
    color: 'from-blue-500 to-blue-600',
  },
  {
    name: 'Donald Layden',
    title: 'Chairman, CFO & Co-Founder',
    description:
      'Donald Layden is an experienced executive with an MBA from Southern New Hampshire University and PMP certification. His career includes leadership roles at Comcast, Dyn (acquired by Oracle), and Smith & Nephew, and he was the first non-employee consultant recognized by Hewlett-Packard as a Distinguished Technologist.',
    initials: 'DL',
    color: 'from-purple-500 to-purple-600',
  },
  {
    name: 'Alexandria B. Lynn',
    title: 'President, CLO & Co-Founder',
    description:
      'Alexandria B. Lynn is a corporate attorney and strategist with expertise in governance, compliance, and organizational development. As co-founder, she provides legal oversight while aligning regulatory frameworks with business growth and innovation.',
    initials: 'AL',
    color: 'from-emerald-500 to-emerald-600',
  },
  {
    name: 'Christopher Grant',
    title: 'CTO & Co-Founder',
    description:
      'Christopher Grant is a technologist with expertise spanning full-stack engineering, distributed networks, and financial DevOps. His career includes network engineering experience at Single Digits, working on complex connectivity solutions, as well as building resilient platforms in financial technology environments.',
    initials: 'CG',
    color: 'from-amber-500 to-amber-600',
  },
];

// Source for all 5 advisor bios: https://www.madhatmediainc.com/about.html.
// Riccardo Ricci + Claudio Antonini added in Phase 7 (2026-05-26).
const ADVISORS = [
  {
    name: 'Craig Goodman',
    title: 'Advisor and Investor',
    description:
      'Founder and President of the National Energy Marketers Association (NEM). Previously held senior policy roles under the Reagan and Bush administrations, shaping national energy and tax regulation. Deep experience with FCC, FERC, and DOE.',
    initials: 'CG',
  },
  {
    name: 'William Asher',
    title: 'Advisor and Investor',
    description:
      "Senior partner at Choate Hall & Stewart and one of the nation's leading corporate attorneys. Harvard-trained, recognized as Boston's \"Lawyer of the Year\" in Corporate Compliance and Securities Regulation.",
    initials: 'WA',
  },
  {
    name: 'Stefan Eriksson',
    title: 'Advisor and Investor',
    description:
      'Swedish entrepreneur who co-founded Vivo Software, one of the first companies to commercialize online video streaming. Following acquisition by RealNetworks, became Chairman for Europe and helped scale streaming media worldwide.',
    initials: 'SE',
  },
  {
    name: 'Riccardo Ricci',
    title: 'Advisor and GP, Ricci Capital Partners',
    description:
      'Riccardo brings the perspective of a successful operator who has built, scaled, and exited multiple businesses. His entrepreneurial track record includes founding and leading two companies to over $100M in annual revenue, culminating in successful institutional exits. As a General Partner, he has managed real estate investments exceeding $300M in asset value. Riccardo demonstrated his M&A expertise by orchestrating the sale of Future Home Power to publicly-traded SIRC, where he also managed post-acquisition integration to ensure seamless value creation.',
    initials: 'RR',
  },
  {
    name: 'Claudio Antonini',
    title: 'Advisor and Senior Advisor, Ricci Capital Partners',
    description:
      "Claudio brings over 17 years of distinguished experience in mergers and acquisitions, having successfully executed more than 200 M&A transactions across both buy-side and sell-side engagements. Claudio also held senior positions at two of the Big Four's premier M&A advisory practices, EY and Deloitte. His transaction experience encompasses landmark deals including PetSmart, the Morgan Stanley Smith Barney joint venture, Charter-Time Warner Cable, P&G's Duracell divestiture, Einstein Bros Bagel, and Bayer Animal Health.",
    initials: 'CA',
  },
];

// ─── Subcomponents ──────────────────────────────────────────────────────────

function SectionAnchor({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      {children}
    </section>
  );
}

function SectionHeader({
  icon: Icon,
  eyebrow,
  title,
  subtitle,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  eyebrow: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="mb-10">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
        <Icon size={14} />
        {eyebrow}
      </div>
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">{title}</h2>
      {subtitle && (
        <p className="text-gray-600 text-lg max-w-3xl leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

const PRODUCT_ANCHORS = new Set([
  'thesis',
  'thesis-detail',
  'architecture',
  'substrate',
  'neutrality',
  'patent',
  'roadmap',
]);

// ─── Main Component ──────────────────────────────────────────────────────────

export default function AboutUs() {
  const location = useLocation();
  const navigate = useNavigate();

  // Phase 5 fallback — legacy bookmarks at /about-us#thesis (or any of the
  // other 6 product anchors) belong on /about-ddarp now.
  useEffect(() => {
    if (!location.hash) return;
    const id = decodeURIComponent(location.hash.slice(1));
    if (PRODUCT_ANCHORS.has(id)) {
      navigate(`/about-ddarp#${id}`, { replace: true });
      return;
    }
    const scrollTo = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    requestAnimationFrame(() => {
      scrollTo();
      const t = window.setTimeout(scrollTo, 260);
      return () => window.clearTimeout(t);
    });
  }, [location.hash, navigate]);

  return (
    <div className="min-h-screen bg-white">
      {/* ─── Top brand banner ─────────────────────────────────────────── */}
      <div className="relative w-full h-48 md:h-64 lg:h-72 overflow-hidden bg-[#0A1929]">
        <img
          src="/images/hero/hero-continuity-field.webp"
          alt=""
          aria-hidden
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-white pointer-events-none" />
      </div>

      {/* ─── Page hero ───────────────────────────────────────────────── */}
      <section className="relative pt-12 pb-12 bg-gradient-to-b from-primary-50/40 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <ScrollReveal>
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-primary/20 shadow-sm mb-6"
              >
                <Building2 size={16} className="text-primary" />
                <span className="text-sm font-medium text-primary">About Us</span>
              </motion.div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                <GradientText text="The team behind the substrate" />
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                MadHat Media, Inc. is a Delaware C-Corporation founded in 2014
                and headquartered in Boston. DDARP is our flagship infrastructure
                platform. This page covers who we are, our leadership, our
                advisors, and our engineering partner.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ─── §1 MadHat Media, Inc. (#company) ────────────────────────── */}
      <SectionAnchor id="company">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <SectionHeader
            icon={Building2}
            eyebrow="The company"
            title="MadHat Media, Inc."
            subtitle="A Delaware C-Corporation founded in 2014, headquartered in Boston. DDARP is our flagship infrastructure platform."
          />

          <GlowCard className="p-8 md:p-10 mb-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-primary-50 text-primary">
                  <MapPin size={22} />
                </div>
                <div>
                  <h3 className="text-gray-900 font-semibold mb-1">Headquarters</h3>
                  <p className="text-slate-600 text-sm">
                    265 Franklin Street, Suite 1702
                    <br />
                    Boston, MA 02110
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-primary-50 text-primary">
                  <Calendar size={22} />
                </div>
                <div>
                  <h3 className="text-gray-900 font-semibold mb-1">Founded</h3>
                  <p className="text-slate-600 text-sm">2014</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-primary-50 text-primary">
                  <FileText size={22} />
                </div>
                <div>
                  <h3 className="text-gray-900 font-semibold mb-1">Incorporation</h3>
                  <p className="text-slate-600 text-sm">Delaware C-Corporation</p>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-6">
              <p className="text-gray-700 leading-relaxed">
                MadHat Media, Inc. is a technology development and holding
                company. DDARP is our flagship infrastructure platform.
                MadHat Media, Inc. also operates MadHat TV, a wholly-owned
                subsidiary focused on streaming and media-delivery
                infrastructure, which serves as a real-world validation
                environment for the broader communications technologies
                the parent develops.
              </p>
            </div>
          </GlowCard>

          {/* Leadership */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium mb-3">
              <Users size={14} />
              Leadership Team
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900">Executive Leadership</h3>
          </div>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {LEADERSHIP.map((person) => (
              <StaggerItem key={person.name}>
                <GlowCard className="p-6 h-full">
                  <div className="flex items-start gap-4">
                    <div
                      className={
                        'w-16 h-16 rounded-2xl bg-gradient-to-br ' +
                        person.color +
                        ' flex items-center justify-center flex-shrink-0'
                      }
                    >
                      <span className="text-white font-bold text-lg">{person.initials}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">{person.name}</h4>
                      <p className="text-primary text-sm font-medium mb-3">{person.title}</p>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {person.description}
                      </p>
                    </div>
                  </div>
                </GlowCard>
              </StaggerItem>
            ))}
          </StaggerContainer>

          {/* Advisors */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium mb-3">
              <Award size={14} />
              Advisory Board
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900">Strategic Advisors</h3>
          </div>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {ADVISORS.map((person) => (
              <StaggerItem key={person.name}>
                <GlowCard className="p-6 h-full">
                  <div className="text-center mb-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center mx-auto mb-3">
                      <span className="text-white font-bold">{person.initials}</span>
                    </div>
                    <h4 className="text-base font-semibold text-gray-900">{person.name}</h4>
                    <p className="text-primary text-xs font-medium">{person.title}</p>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed text-center">
                    {person.description}
                  </p>
                </GlowCard>
              </StaggerItem>
            ))}
          </StaggerContainer>

          {/* MadHat TV subsidiary */}
          <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-8 mb-12">
            <div className="flex items-start gap-6">
              <div className="p-4 rounded-2xl bg-purple-50 text-purple-500 flex-shrink-0">
                <Briefcase size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">MadHat TV, Inc.</h3>
                <p className="text-primary text-xs font-medium mb-3">
                  Wholly-Owned Subsidiary (Indiana)
                </p>
                <p className="text-slate-600 leading-relaxed text-sm">
                  MadHat TV, Inc. extends the group&apos;s reach into media and
                  streaming services. Established as both a commercial operation
                  and a platform for validating advanced communications
                  technologies, MadHat TV focuses on live and on-demand content
                  delivery, distributed streaming systems, and media distribution
                  channels, demonstrating MadHat Media, Inc.&apos;s infrastructure
                  innovations in real-world environments.
                </p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Get in touch</h3>
            <p className="text-slate-600 mb-6 text-sm">
              Investor inquiries and partnership conversations.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="mailto:investor.relations@madhatmediainc.com"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-900 text-sm transition-colors"
              >
                <Mail size={16} />
                Investor Relations
              </a>
              <a
                href="https://linkedin.com/company/madhatmedia"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-900 text-sm transition-colors"
              >
                <Linkedin size={16} />
                LinkedIn
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white hover:bg-primary-dark rounded-xl text-sm transition-colors"
              >
                <Mail size={16} />
                Send a message
              </Link>
            </div>
          </div>
        </div>
      </SectionAnchor>

      <div className="border-t border-slate-100" />

      {/* ─── §2 SapientSwift Solutions (#engineering) ────────────────── */}
      <SectionAnchor id="engineering">
        <div className="max-w-5xl mx-auto px-4 py-16">
          <SectionHeader
            icon={Wrench}
            eyebrow="Engineering partner"
            title="Developed and maintained by SapientSwift Solutions"
          />

          <div className="rounded-2xl border border-gray-200 bg-white p-8">
            <div className="flex items-start gap-6">
              <div className="p-4 rounded-2xl bg-cyan-50 text-cyan-600 flex-shrink-0">
                <Wrench size={28} />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  SapientSwift Solutions Private Limited
                </h3>
                <a
                  href="https://sapientswift.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary text-sm font-medium hover:underline mb-3"
                >
                  sapientswift.com
                  <ExternalLink size={12} />
                </a>
                <p className="text-slate-600 text-sm leading-relaxed">
                  DDARP&apos;s observation substrate, manager web application,
                  Tech Documentation tab, and surrounding tooling are developed
                  and maintained by SapientSwift Solutions. The same team is
                  responsible for the 13-wave multi-cloud rollout and the
                  operational systems that keep the substrate online.
                </p>
              </div>
            </div>
          </div>
        </div>
      </SectionAnchor>

      {/* ─── Final CTA ───────────────────────────────────────────────── */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Curious about the protocol itself?
            </h2>
            <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
              We have built DDARP across 118 nodes and three clouds. The
              About DDARP page covers the thesis, the six-layer architecture,
              the IP, and the roadmap.
            </p>
            <PublicCTAPair learnMoreHref="/about-ddarp" />
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
